import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { Controller, useWatch } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setDialog } from "src/features/dialog/dialogSlice";
import {
  useGetTenantInformationQuery,
  useSendTenantTestMailMutation,
  useUpdateTenantMailSettingsMutation,
} from "src/features/tenants/tenantsApiSlice";
import { selectCurrentUserData } from "src/features/auth/authSlice";

const MailConfigurationSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUserData = useSelector(selectCurrentUserData);
  const { data: tenantInfo, isLoading } = useGetTenantInformationQuery();
  const [updateTenantMailSettings, { isLoading: isSaving }] =
    useUpdateTenantMailSettingsMutation();
  const [sendTenantTestMail, { isLoading: isTesting }] =
    useSendTenantTestMailMutation();

  const defaultValues = useMemo(
    () => ({
      fromName: tenantInfo?.settings?.mail?.fromName || "",
      fromMail: tenantInfo?.settings?.mail?.fromMail || "",
      host: tenantInfo?.settings?.mail?.host || "",
      port: tenantInfo?.settings?.mail?.port || 587,
      user: tenantInfo?.settings?.mail?.user || "",
      pass: tenantInfo?.settings?.mail?.pass || "",
      replyTo: tenantInfo?.settings?.mail?.replyTo || "",
      sendCopyToSender: tenantInfo?.settings?.mail?.sendCopyToSender || false,
      testRecipient: currentUserData?.mail || "",
    }),
    [tenantInfo, currentUserData]
  );

  const { register, handleSubmit, control, reset, getValues } = useForm({
    defaultValues,
  });

  const PORT_OPTIONS = [25, 465, 587];

  const [host, port, user, pass, fromMail, testRecipient] = useWatch({
    control,
    name: ["host", "port", "user", "pass", "fromMail", "testRecipient"],
  });
  const isSmtpReady = Boolean(
    String(host || "").trim() &&
      port &&
      String(user || "").trim() &&
      String(pass || "").trim() &&
      String(fromMail || "").trim() &&
      String(testRecipient || "").trim()
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = async (data) => {
    const { testRecipient, ...mailSettings } = data;
    try {
      const result = await updateTenantMailSettings(mailSettings).unwrap();
      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: "Mail Einstellungen gespeichert",
            type: "SUCCESS",
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText:
              error?.data?.message ||
              "Mail Einstellungen konnten nicht gespeichert werden.",
          },
        })
      );
    }
  };

  const handleSendTest = async () => {
    const { testRecipient: recipient, ...settings } = getValues();

    if (!recipient?.trim()) {
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: { errorText: "Bitte eine Test-E-Mail-Adresse angeben." },
        })
      );
      return;
    }

    try {
      const result = await sendTenantTestMail({ recipient: recipient.trim(), settings }).unwrap();
      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: "Test Mail gesendet",
            type: "SUCCESS",
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText:
              error?.data?.message || "Test Mail konnte nicht gesendet werden.",
          },
        })
      );
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          onClick={() => navigate("/dashboard/settings/mail")}
        >
          ZURÜCK
        </Button>
        <Typography variant="h3">Mail Konfiguration</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="body1" sx={{ mb: 2 }}>
        Hinterlegen Sie die Zugangsdaten für den Versand der
        Kündigungsbestätigung.
      </Typography>
      <Alert
        severity="info"
        icon={<Icon icon="mdi:information-outline" />}
        sx={{ mb: 3 }}
      >
        Für den Mailversand benötigen Sie SMTP-Zugangsdaten Ihres Mail-Anbieters
        (z.&thinsp;B. Strato, IONOS, Google Workspace). Diese finden Sie in der
        Verwaltungsoberfläche Ihres Hosting- oder E-Mail-Anbieters unter den
        Einstellungen für ausgehende E-Mails / SMTP. Suchen Sie bei Bedarf nach
        „[Ihr Anbieter] SMTP Einstellungen".
      </Alert>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Absender Name"
              fullWidth
              InputLabelProps={{ shrink: true }}
              {...register("fromName")}
              disabled={isSaving || isTesting}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Absender E-Mail"
              fullWidth
              InputLabelProps={{ shrink: true }}
              {...register("fromMail")}
              disabled={isSaving || isTesting}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="SMTP-Server"
              fullWidth
              InputLabelProps={{ shrink: true }}
              {...register("host")}
              disabled={isSaving || isTesting}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="port"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth disabled={isSaving || isTesting}>
                  <InputLabel shrink>Port</InputLabel>
                  <Select {...field} label="Port" notched>
                    {PORT_OPTIONS.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="SMTP-Benutzername"
              fullWidth
              InputLabelProps={{ shrink: true }}
              {...register("user")}
              disabled={isSaving || isTesting}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="SMTP-Kennwort"
              type="password"
              fullWidth
              InputLabelProps={{ shrink: true }}
              {...register("pass")}
              disabled={isSaving || isTesting}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Reply-To"
              fullWidth
              InputLabelProps={{ shrink: true }}
              {...register("replyTo")}
              disabled={isSaving || isTesting}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="sendCopyToSender"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth disabled={isSaving || isTesting}>
                  <InputLabel shrink>Kopie an Absender</InputLabel>
                  <Select {...field} label="Kopie an Absender" notched>
                    <MenuItem value={false}>Nein</MenuItem>
                    <MenuItem value>Ja</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              label="Test E-Mail"
              fullWidth
              InputLabelProps={{ shrink: true }}
              {...register("testRecipient")}
              disabled={isSaving || isTesting}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Tooltip
              title={
                !isSmtpReady
                  ? "Bitte alle Felder ausfüllen, um eine Testmail zu senden."
                  : ""
              }
            >
              <span>
                <Button
                  variant="outlined"
                  onClick={handleSendTest}
                  disabled={!isSmtpReady || isSaving || isTesting}
                >
                  Test Mail senden
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            disabled={isSaving || isTesting}
          >
            Einstellungen speichern
          </Button>
        </Box>
      </form>
    </>
  );
};

export default MailConfigurationSettings;
